import uniqid from 'uniqid'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import GitHubIcon from '@material-ui/icons/GitHub'
import LaunchIcon from '@material-ui/icons/Launch'
import { useParams, Link } from 'react-router-dom'
import { projects } from '../../portfolio'
import './ProjectShowCase.css'

const ProjectShowCase = () => {
  const { id } = useParams()
  const index = parseInt(id, 10)
  const project = Number.isInteger(index) ? projects[index] : null

  const [modal, setModal] = useState(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [currentGalleryImages, setCurrentGalleryImages] = useState([])
  const modalRef = useRef(null)

  const openModal = (img, images, imgIndex) => {
    setModal(img)
    setCurrentGalleryImages(images)
    setCurrentImageIndex(imgIndex)
  }

  const closeModal = () => {
    setModal(null)
    setCurrentGalleryImages([])
    setCurrentImageIndex(0)
  }

  const nextImage = useCallback(() => {
    if (currentGalleryImages.length > 0) {
      const newIndex = (currentImageIndex + 1) % currentGalleryImages.length
      setCurrentImageIndex(newIndex)
      setModal(currentGalleryImages[newIndex])
    }
  }, [currentImageIndex, currentGalleryImages])

  const prevImage = useCallback(() => {
    if (currentGalleryImages.length > 0) {
      const newIndex = (currentImageIndex - 1 + currentGalleryImages.length) % currentGalleryImages.length
      setCurrentImageIndex(newIndex)
      setModal(currentGalleryImages[newIndex])
    }
  }, [currentImageIndex, currentGalleryImages])

  const getGalleryClassName = (imageCount) => {
    if (imageCount === 1) return 'project-gallery single-image'
    if (imageCount === 2) return 'project-gallery two-images'
    return 'project-gallery'
  }

  useEffect(() => {
    // Scroll to top when component mounts or project changes
    window.scrollTo(0, 0)
  }, [id])

  useEffect(() => {
    if (modal && modalRef.current) {
      // focus modal content for accessibility
      modalRef.current.focus()
      // Disable body scroll when modal is open
      document.body.style.overflow = 'hidden'
    } else {
      // Re-enable body scroll when modal is closed
      document.body.style.overflow = 'unset'
    }

    const handleKey = (e) => {
      if (e.key === 'Escape') closeModal()
      if (e.key === 'ArrowRight') nextImage()
      if (e.key === 'ArrowLeft') prevImage()
    }

    if (modal) {
      document.addEventListener('keydown', handleKey)
    }

    return () => {
      document.removeEventListener('keydown', handleKey)
      // Cleanup: ensure scroll is re-enabled when component unmounts
      document.body.style.overflow = 'unset'
    }
  }, [modal, nextImage, prevImage])

  if (!project) return null

  return (
    <section className='section project-showcase'>
      <div className='center'>
        {/* Project Header */}
        <div className='project-showcase__header'>
          <h2 className='section__title'>{project.name}</h2>
          <p className='project__description'>{project.description}</p>

          {project.stack && (
            <div className='project__stack-wrapper'>
              <h3 className='project__stack-title'>Technologies</h3>
              <ul className='project__stack'>
                {project.stack.map((item) => (
                  <li key={uniqid()} className='project__stack-item'>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className='project__links'>
            {project.sourceCode && (
              <a href={project.sourceCode} className='link link--icon'>
                <GitHubIcon />
              </a>
            )}

            {project.livePreview && (
              <a href={project.livePreview} className='link link--icon'>
                <LaunchIcon />
              </a>
            )}
          </div>
        </div>

        {/* If project has sections, render each with a gallery */}
        {project.sections && project.sections.length ? (
          project.sections.map((sec) => (
            <section key={uniqid()} className='project-section'>
              {sec.title && <h3 className='project-section__title'>{sec.title}</h3>}
              {sec.text && <p className='project-section__text'>{sec.text}</p>}

              {sec.images && sec.images.length > 0 && (
                <div className={getGalleryClassName(sec.images.length)}>
                  {sec.images.map((img, imgIndex) => (
                    <button
                      key={uniqid()}
                      type='button'
                      className='project-gallery__item'
                      onClick={() => openModal(img, sec.images, imgIndex)}
                    >
                      <img
                        src={
                          img.src && img.src.startsWith('http')
                            ? img.src
                            : `${process.env.PUBLIC_URL}/images/${img.src}`
                        }
                        alt={img.caption || project.name}
                      />
                      {(img.caption || img.text) && (
                        <figcaption className='project-gallery__caption'>
                          {img.caption && <div className='project-gallery__caption-title'>{img.caption}</div>}
                          {img.text && <div className='project-gallery__caption-text'>{img.text}</div>}
                        </figcaption>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </section>
          ))
        ) : (
          /* fallback: single image using gallery structure */
          project.image && (
            <div className='project-gallery single-image'>
              <button
                type='button'
                className='project-gallery__item'
                onClick={() => openModal({ src: project.image }, [{ src: project.image }], 0)}
              >
                <img
                  src={
                    project.image.startsWith('http')
                      ? project.image
                      : `${process.env.PUBLIC_URL}/images/${project.image}`
                  }
                  alt={`${project.name} screenshot`}
                />
              </button>
            </div>
          )
        )}

        <p className='project__back-link'>
          <Link to='/projects' style={{ marginLeft: '4rem' }} className='link'>Back to projects</Link>
        </p>
      </div>

      {/* modal preview for gallery images */}
      {modal && (
        <button
          type='button'
          className='project-modal'
          aria-hidden='true'
          onClick={(e) => { if (e.target === e.currentTarget) closeModal() }}
          tabIndex={-1}
        >
          <div
            className='project-modal__content'
            tabIndex={-1}
            ref={modalRef}
            role='dialog'
            aria-modal='true'
          >
            <button type='button' className='project-modal__close' onClick={() => closeModal()}>
              ×
            </button>

            <div className='project-modal__image-container'>
              {currentGalleryImages.length > 1 && (
                <>
                  <button type='button' className='project-modal__nav project-modal__nav--prev' onClick={prevImage}>
                    ‹
                  </button>
                  <button type='button' className='project-modal__nav project-modal__nav--next' onClick={nextImage}>
                    ›
                  </button>
                  <div className='project-modal__counter'>
                    {currentImageIndex + 1} / {currentGalleryImages.length}
                  </div>
                </>
              )}

              <img
                src={
                  modal.src && modal.src.startsWith('http')
                    ? modal.src
                    : `${process.env.PUBLIC_URL}/images/${modal.src}`
                }
                alt={modal.caption || project.name}
              />
            </div>

            <div className='project-modal__info'>
              {(modal.caption || modal.modalText || modal.text) && (
                <div className='project-modal__caption'>
                  {modal.caption && <div>{modal.caption}</div>}
                  {(modal.modalText || modal.text) && (
                    <div style={{ fontSize: 'medium', marginTop: '0.5rem' }}>
                      {modal.modalText || modal.text}
                    </div>
                  )}
                </div>
              )}
              {modal.link && (
                <div className='project-modal__link'>
                  <a href={modal.link} target='_blank' rel='noreferrer' className='link'>Open</a>
                </div>
              )}
            </div>
          </div>
        </button>
      )}
    </section>
  )
}

export default ProjectShowCase
